/**
 * StoreMenuScroll hook
 *
 * This hook is designed to enhance user interaction with the store menu page
 * by managing scroll actions. It will show and hide the top bars depending on the
 * scroll direction and the current position.
 *
 * The hook supports the following options as data attributes:
 *
 *   * data-menu - ID of the top menu selector bar
 *   * data-hours - ID of the top hours bar
 *   * data-search - ID of the top search bar
 *   * data-product - ID of the top product list selecor bar
 *   * data-original-menu - ID of the original menu selector bar
 *   * data-original-search - ID of the original search product bar
 *   * data-original-product - ID of the original product list selecor bar
 *   * data-top - ID of the container of all the top bars
 * 
 * Example usage:
 *
<div
      id="store-web-content"
      phx-hook="StoreMenuScroll"
      data-menu="top-menu-bar"
      data-hours="top-hours-bar"
      data-search="top-search-bar"
      data-product="top-product-bar"
      data-original-menu="original-menu-bar"
      data-original-search="original-search-bar"
      data-original-product="original-product-bar"
      data-top="top-bar"
    >
 */

// Horizontally scrolls a specified target element into the center of its container
function generateScrollFunc(scrollTarget, behavior = "smooth", offset = 0) {
  return function scrollFunc(targetElement) {
    const itemWidth = targetElement.getBoundingClientRect().width;
    const containerWidth = scrollTarget.getBoundingClientRect().width;
    const pos =
      targetElement.getBoundingClientRect().left -
      scrollTarget.getBoundingClientRect().left -
      offset +
      itemWidth / 2 -
      containerWidth / 2;
    scrollTarget.scrollBy({ behavior: behavior, left: pos });
  };
}

// Shows a previously hidden element with a smooth transition.
function smoothShowElement(element) {
  if (element.classList.contains("hidden-transition")) {
    element.style.maxHeight = "none";
    const actualHeight = element.scrollHeight + "px";
    element.style.maxHeight = "0";

    // Start the transition to show
    setTimeout(() => {
      element.classList.remove("hidden-transition");
      element.style.maxHeight = actualHeight;
    }, 10); // Short delay to allow max-height to take effect
  }
}

// Hides a previously hidden element with a smooth transition.
function smoothHideElement(element) {
  if (!element.classList.contains("hidden-transition")) {
    element.style.maxHeight = element.scrollHeight + "px";
    // Start the transition to hide
    setTimeout(() => {
      element.classList.add("hidden-transition");
      element.style.maxHeight = "0";
    }, 10); // Short delay to allow max-height to take effect
  }
}

export const StoreMenuScroll = {
  mounted() {
    const topMenuBar = document.getElementById(this.el.dataset.menu);
    const topHoursBar = document.getElementById(this.el.dataset.hours);
    const topSearchBar = document.getElementById(this.el.dataset.search);
    const topProductBar = document.getElementById(this.el.dataset.product);
    const originalMenuBar = document.getElementById(
      this.el.dataset.originalMenu,
    );
    const originalSearchBar = document.getElementById(
      this.el.dataset.originalSearch,
    );
    const originalProductBar = document.getElementById(
      this.el.dataset.originalProduct,
    );
    const topBar = document.getElementById(this.el.dataset.top);

    let isScrollEventDisabled = false;

    let lastScrollTop = 0;
    const SCROLL_THRESHOLD = 10; // Pixels of scrolling required to trigger the event
    let isAboveOriginalMenuBar = true;

    const currentMenu = document.getElementById(this.el.dataset.currentMenu);
    const menuScrollBox = document.getElementById(
      "menu-selector-bar-scroll-box-main",
    );
    // Center the currently selected menu when view is mounted
    if (currentMenu && menuScrollBox) {
      let eventScrollFunc = generateScrollFunc(menuScrollBox, "auto");
      eventScrollFunc(currentMenu);
    }

    // Control the visibility of top menu bars during scroll events.
    const handleMenuScroll = () => {
      // When original product bar is not avaliable, it means menu page is in search mode, remove all top bars
      if (!document.getElementById(this.el.dataset.originalProduct)) {
        topBar.classList.add("hidden");
        return;
      }

      let currentScrollTop = this.el.scrollTop;
      let originalMenuBarBottom =
        originalMenuBar.offsetTop + originalMenuBar.offsetHeight;
      let originalProductBarPostion =
        originalProductBar.getBoundingClientRect().top;
      let topBarHeight = topBar.offsetHeight;

      isAboveOriginalMenuBar = currentScrollTop < originalMenuBarBottom;

      // Disable scroll events temporarily when a product list selector is being clicked to stabilize UI.
      // Ensure either top product bar or original product bar is visible on the screen
      if (isScrollEventDisabled) {
        if (originalProductBarPostion >= topBarHeight) {
          topProductBar.classList.add("hidden-transition");
        } else {
          topProductBar.classList.remove("hidden-transition");
        }
        return;
      }

      // Use a scroll threshold to avoid accidental triggering of UI changes.
      if (Math.abs(lastScrollTop - currentScrollTop) > SCROLL_THRESHOLD) {
        if (currentScrollTop > lastScrollTop) {
          // Scrolling down
          smoothHideElement(topSearchBar);
        } else {
          // Scrolling up
          if (!isAboveOriginalMenuBar) {
            smoothShowElement(topSearchBar);
          }
        }
        lastScrollTop = currentScrollTop;
      }

      // Show and hide various top bars depending on the current position
      // There should always have one and only one product list bar on the screen
      if (
        currentScrollTop + topMenuBar.offsetHeight >=
          originalProductBar.offsetTop &&
        currentScrollTop >= originalSearchBar.offsetTop
      ) {
        smoothShowElement(topHoursBar);
        smoothShowElement(topProductBar);
      } else {
        if (originalProductBarPostion >= topBarHeight) {
          smoothHideElement(topHoursBar);
          smoothHideElement(topSearchBar);
          smoothHideElement(topProductBar);
        }
      }

      // Hide top menu bar when original menu bar is visible, show top menu bar
      // when original menu bar is not visble
      if (currentScrollTop < originalMenuBarBottom - topMenuBar.offsetHeight) {
        smoothHideElement(topMenuBar);
      } else {
        smoothShowElement(topMenuBar);
      }
    };

    // Listen for the 'product-selector-clicked' custom event on the window.
    // Temporarily disable scroll events and hides various top bars to optimze screen space for menu content viewing.
    window.addEventListener("product-selector-clicked", (data) => {
      if (data.detail.top_bar) {
        isScrollEventDisabled = true;
        setTimeout(() => {
          isScrollEventDisabled = false;
        }, 100);
        smoothHideElement(topHoursBar);
        smoothHideElement(topSearchBar);
      }
    });

    // Listens for the 'scroll-product-selector' custom event on the window.
    // Horizontally scroll the prodcut list selector to ensure that the selected product list is centered in the view.
    window.addEventListener("scroll-product-selector", (event) => {
      const targetId = `#${event.detail.target}`;
      const scrollTargetId = `#${event.detail.scrollTarget}`;
      const targetElement = document.querySelector(targetId);
      const scrollTargetElement = document.querySelector(scrollTargetId);

      if (targetElement && scrollTargetElement) {
        let eventScrollFunc = generateScrollFunc(scrollTargetElement);
        eventScrollFunc(targetElement);
      }
    });

    this.el.addEventListener("scroll", handleMenuScroll, false);
  },

  destroyed() {
    this.el.removeEventListener("scroll", this.handleMenuScroll);
  },
};

export default StoreMenuScroll;
